// src/pages/DashboardPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, onSnapshot, doc, deleteDoc, updateDoc, getDocs } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import './DashboardPage.css';
import ManageCategoriesPage from './ManageCategoriesPage';
import AnalyticsPanel from '../components/AnalyticsPanel';

// --- User Management and Modal Components ---
function EditUserModal({ user, isOpen, onClose, onSave, isSaving }) {
    const [formData, setFormData] = useState({ firstName: '', lastName: '', role: 'contributor' });
    useEffect(() => { if (user) { setFormData({ firstName: user.firstName || '', lastName: user.lastName || '', role: user.role || 'contributor' }); } }, [user]);
    if (!isOpen || !user) return null;
    const handleChange = (e) => { const { name, value } = e.target; setFormData(prev => ({ ...prev, [name]: value })); };
    const handleSave = (e) => { e.preventDefault(); onSave({ uid: user.id, ...formData }); };
    return (
        <div className="modal-overlay">
            <div className="modal-content edit-user-modal">
                <h2>Edit User</h2>
                <form onSubmit={handleSave} className="modal-form">
                    <div className="form-field"><label>First Name</label><input name="firstName" type="text" value={formData.firstName} onChange={handleChange} required /></div>
                    <div className="form-field"><label>Last Name</label><input name="lastName" type="text" value={formData.lastName} onChange={handleChange} required /></div>
                    <div className="form-field">
                        <label>Role</label>
                        <select name="role" value={formData.role} onChange={handleChange}>
                            <option value="contributor">Contributor</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                    <div className="modal-actions">
                        <button type="button" className="btn btn-cancel" onClick={onClose} disabled={isSaving}>Cancel</button>
                        <button type="submit" className="btn btn-save" disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Changes'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function UserManagementPanel() {
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState('contributor');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [usersList, setUsersList] = useState([]);
    const [listLoading, setListLoading] = useState(true);
    const [deletingUserId, setDeletingUserId] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [isSavingUser, setIsSavingUser] = useState(false);

    useEffect(() => {
        const fetchUsers = async () => {
            setListLoading(true);
            try {
                const usersCollectionRef = collection(db, 'contributors');
                const userSnapshot = await getDocs(usersCollectionRef);
                setUsersList(userSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (err) { setError("Could not load users."); } finally { setListLoading(false); }
        };
        fetchUsers();
    }, [successMessage]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const createNewUser = httpsCallable(functions, 'createNewUser');
            await createNewUser({ email, password, firstName, lastName, role });
            setSuccessMessage(`Created user: ${firstName}`);
            setFirstName(''); setLastName(''); setEmail(''); setPassword('');
        } catch (err) { setError(err.message); } finally { setIsSubmitting(false); }
    };

    const handleSaveUser = async (updatedUserData) => {
        setIsSavingUser(true);
        try {
            const updateUserFunction = httpsCallable(functions, 'updateUser');
            await updateUserFunction(updatedUserData);
            setUsersList(current => current.map(u => u.id === updatedUserData.uid ? { ...u, ...updatedUserData } : u));
            setIsEditModalOpen(false);
        } catch (err) { alert(err.message); } finally { setIsSavingUser(false); }
    };

    const handleDeleteUser = async (userId) => {
        if (window.confirm("Delete user?")) {
            setDeletingUserId(userId);
            try {
                const deleteUserFunction = httpsCallable(functions, 'deleteUser');
                await deleteUserFunction({ uid: userId });
                setUsersList(current => current.filter(u => u.id !== userId));
            } finally { setDeletingUserId(null); }
        }
    };

    return (
        <>
            <EditUserModal user={editingUser} isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} onSave={handleSaveUser} isSaving={isSavingUser} />
            <div className="user-management-section">
                <h2 className="list-header">Add a New User</h2>
                <form onSubmit={handleSubmit} className="user-form">
                    <div className="form-grid">
                        {error && <div className="form-error-message">{error}</div>}
                        {successMessage && <div className="form-success-message">{successMessage}</div>}
                        
                        <div className="form-field">
                            <input placeholder="First Name" value={firstName} onChange={e => setFirstName(e.target.value)} required />
                        </div>
                        <div className="form-field">
                            <input placeholder="Last Name" value={lastName} onChange={e => setLastName(e.target.value)} required />
                        </div>
                        <div className="form-field">
                            <input placeholder="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                        </div>
                        <div className="form-field">
                            <input placeholder="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
                        </div>
                        <div className="form-field">
                            <select value={role} onChange={e => setRole(e.target.value)}>
                                <option value="contributor">Contributor</option>
                                <option value="admin">Admin</option>
                            </select>
                        </div>
                        <div className="form-field-action">
                            <button type="submit" className="btn-submit-user">{isSubmitting ? '...' : 'Create'}</button>
                        </div>
                    </div>
                </form>
            </div>
            <div className="user-management-section">
                <h2 className="list-header">Current Users</h2>
                <ul className="user-list">
                    {usersList.map(user => (
                        <li key={user.id} className="user-list-item">
                            <div className="user-info">
                                <strong>{user.firstName} {user.lastName}</strong> <br/>
                                {user.email} <span className={`role-badge role-${user.role}`}>{user.role}</span>
                            </div>
                            <div className="user-actions">
                                <button className="btn btn-edit" onClick={() => { setEditingUser(user); setIsEditModalOpen(true); }}>Edit</button>
                                <button className="btn btn-delete" onClick={() => handleDeleteUser(user.id)}>Delete</button>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        </>
    );
}

const DualStatusIndicator = ({ hasCoordinates, hasGeohash, hasCity, hasState }) => {
    const isLocationDataComplete = hasGeohash && hasCity && hasState;
    return (
        <div className="dual-status-container" style={{ display: 'flex', gap: '5px', marginRight: '15px' }}>
            <div 
                className={`gps-status-indicator ${hasCoordinates ? 'gps-status-valid' : 'gps-status-missing'}`} 
                title={hasCoordinates ? 'GPS Present' : 'GPS Missing'}
            ></div>
            <div 
                className={`gps-status-indicator ${isLocationDataComplete ? 'gps-status-valid' : ''}`} 
                title={isLocationDataComplete ? 'Location Data Complete' : 'Missing Geohash, City, or State'}
                style={!isLocationDataComplete ? { backgroundColor: '#FFBF00' } : {}}
            ></div>
        </div>
    );
};

function DashboardPage() {
    const { currentUser } = useAuth();
    const [activeTab, setActiveTab] = useState('resources');
    const [myResources, setMyResources] = useState([]);
    const [pendingResources, setPendingResources] = useState([]);
    const [otherApprovedResources, setOtherApprovedResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [resourceToDelete, setResourceToDelete] = useState(null);

    // --- FILTER STATE VARIABLES ---
    const [categories, setCategories] = useState([]);
    const [contributors, setContributors] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterState, setFilterState] = useState('');
    const [filterCity, setFilterCity] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [filterContributor, setFilterContributor] = useState('');
    const [filterMissingData, setFilterMissingData] = useState(false);

    // Fetch Categories and Users for the Dropdowns
    useEffect(() => {
        const fetchFilterData = async () => {
            try {
                const catSnap = await getDocs(collection(db, 'categories'));
                setCategories(catSnap.docs.map(d => ({ id: d.id, ...d.data() })));

                if (currentUser?.role === 'admin') {
                    const userSnap = await getDocs(collection(db, 'contributors'));
                    setContributors(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));
                }
            } catch (err) { console.error("Could not load filter dropdown data", err); }
        };
        if (currentUser) fetchFilterData();
    }, [currentUser]);

    // Data Fetching via Snapshots
    useEffect(() => {
        if (!currentUser) return;
        const resourcesRef = collection(db, "resources");
        const unsubscribes = [];

        if (currentUser.role === 'admin') {
            unsubscribes.push(onSnapshot(query(resourcesRef, where("status", "==", "pending")), (s) => setPendingResources(s.docs.map(d => ({ id: d.id, ...d.data() })))));
            unsubscribes.push(onSnapshot(query(resourcesRef, where("createdBy", "==", currentUser.uid)), (s) => setMyResources(s.docs.map(d => ({ id: d.id, ...d.data() })))));
            unsubscribes.push(onSnapshot(query(resourcesRef, where("status", "==", "approved"), where("createdBy", "!=", currentUser.uid)), (s) => {
                setOtherApprovedResources(s.docs.map(d => ({ id: d.id, ...d.data() })));
                setLoading(false);
            }));
        } else {
            unsubscribes.push(onSnapshot(query(resourcesRef, where("createdBy", "==", currentUser.uid)), (s) => {
                setMyResources(s.docs.map(d => ({ id: d.id, ...d.data() })));
                setLoading(false);
            }));
        }
        return () => unsubscribes.forEach(un => un());
    }, [currentUser]);

    const handleApprove = async (id) => { await updateDoc(doc(db, "resources", id), { status: "approved" }); };
    const confirmDelete = async () => { if (resourceToDelete) { await deleteDoc(doc(db, "resources", resourceToDelete.id)); setShowConfirmModal(false); } };

    // --- CLIENT SIDE FILTERING ENGINE ---
    const allApprovedResources = currentUser?.role === 'admin' ? [...myResources, ...otherApprovedResources] : myResources;

    const applyFilters = (resourcesList) => {
        return resourcesList.filter(res => {
            const searchLower = searchTerm.toLowerCase();
            const matchesSearch = searchTerm === '' || 
                res.organizationName?.toLowerCase().includes(searchLower) || 
                res.message?.toLowerCase().includes(searchLower) ||
                res.location?.toLowerCase().includes(searchLower);
            
            const matchesState = filterState === '' || res.state?.toLowerCase() === filterState.toLowerCase();
            const matchesCity = filterCity === '' || res.city?.toLowerCase().includes(filterCity.toLowerCase());
            const matchesCategory = filterCategory === '' || (res.categoryIDs && res.categoryIDs.includes(filterCategory));
            
            let matchesContributor = true;
            if (filterContributor === 'me') {
                matchesContributor = res.createdBy === currentUser.uid;
            } else if (filterContributor !== '') {
                matchesContributor = res.createdBy === filterContributor;
            }

            const isLocationComplete = !!res.geohash && !!res.city && !!res.state && !!res.coordinates;
            const matchesMissingData = !filterMissingData || !isLocationComplete;

            return matchesSearch && matchesState && matchesCity && matchesCategory && matchesContributor && matchesMissingData;
        });
    };

    const filteredApprovedResources = applyFilters(allApprovedResources);
    const filteredPendingResources = applyFilters(pendingResources);

    const ResourceList = ({ resources }) => (
        <ul className="resource-list">
            {resources.map(resource => (
                <li key={resource.id} className="resource-item">
                    {/* New Wrapper for mobile layout */}
                    <div className="resource-main-content">
                        <DualStatusIndicator 
                            hasCoordinates={!!resource.coordinates} 
                            hasGeohash={!!resource.geohash} 
                            hasCity={!!resource.city}
                            hasState={!!resource.state}
                        />
                        <div className="resource-info">
                            <h3 className="resource-name">{resource.organizationName}</h3>
                            {resource.city && resource.state && (
                                <span className="resource-location">
                                    {resource.city}, {resource.state}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="resource-actions">
                        <Link to={`/edit-resource/${resource.id}`} className="btn btn-edit">Edit</Link>
                        <button className="btn btn-delete" onClick={() => { setResourceToDelete(resource); setShowConfirmModal(true); }}>Delete</button>
                    </div>
                </li>
            ))}
        </ul>
    );

    return (
        <div className="dashboard-container">
            {showConfirmModal && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h2>Delete {resourceToDelete?.organizationName}?</h2>
                        <div className="modal-actions">
                            <button className="btn btn-cancel" onClick={() => setShowConfirmModal(false)}>Cancel</button>
                            <button className="btn btn-confirm-delete" onClick={confirmDelete}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
            <div className="dashboard-content">
                <div className="dashboard-header-container">
                    <h1 className="dashboard-header">My Dashboard</h1>
                    {currentUser?.role === 'admin' && (
                        <div className="dashboard-tabs">
                            <button className={`tab-button ${activeTab === 'resources' ? 'active' : ''}`} onClick={() => setActiveTab('resources')}>Resources</button>
                            <button className={`tab-button ${activeTab === 'categoryManagement' ? 'active' : ''}`} onClick={() => setActiveTab('categoryManagement')}>Categories</button>
                            <button className={`tab-button ${activeTab === 'userManagement' ? 'active' : ''}`} onClick={() => setActiveTab('userManagement')}>Users</button>
                            <button className={`tab-button ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')}>Reports</button>
                        </div>
                    )}
                </div>
                
                {activeTab === 'resources' && (
                    <>
                        {/* --- NEW FILTER BAR UI --- */}
                        <div className="dashboard-filter-bar">
                            <input 
                                type="text" 
                                placeholder="Search names, keywords, or address..." 
                                value={searchTerm} 
                                onChange={(e) => setSearchTerm(e.target.value)} 
                                className="filter-input search-input"
                            />
                            <div className="filter-dropdowns">
                                <input 
                                    type="text" 
                                    placeholder="City" 
                                    value={filterCity} 
                                    onChange={(e) => setFilterCity(e.target.value)} 
                                    className="filter-input"
                                />
                                <input 
                                    type="text" 
                                    placeholder="State" 
                                    value={filterState} 
                                    onChange={(e) => setFilterState(e.target.value)} 
                                    className="filter-input"
                                    maxLength={2}
                                />
                                <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="filter-input">
                                    <option value="">All Services</option>
                                    {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                </select>
                                {currentUser?.role === 'admin' && (
                                    <select value={filterContributor} onChange={(e) => setFilterContributor(e.target.value)} className="filter-input">
                                        <option value="">All Contributors</option>
                                        <option value="me">Only My Pins</option>
                                        {contributors.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
                                    </select>
                                )}
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label className="filter-checkbox-label">
                                    <input 
                                        type="checkbox" 
                                        checked={filterMissingData} 
                                        onChange={(e) => setFilterMissingData(e.target.checked)} 
                                    />
                                    Show Missing Location Data (Amber/Red Dots)
                                </label>
                                
                                {(searchTerm || filterState || filterCity || filterCategory || filterContributor || filterMissingData) && (
                                    <button 
                                        className="btn-clear-filters"
                                        onClick={() => {
                                            setSearchTerm(''); setFilterState(''); setFilterCity(''); 
                                            setFilterCategory(''); setFilterContributor(''); setFilterMissingData(false);
                                        }}
                                    >
                                        Clear Filters
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* --- MERGED LISTS --- */}
                        {currentUser?.role === 'admin' && (
                            <div className="resources-section">
                                <h2 className="list-header">Pending Approval ({filteredPendingResources.length})</h2>
                                {filteredPendingResources.length === 0 ? (
                                    <p style={{ padding: '10px 20px', color: '#666' }}>No pending resources match your search.</p>
                                ) : (
                                    <ul className="resource-list">
                                        {filteredPendingResources.map(r => (
                                            <li key={r.id} className="resource-item">
                                                {/* New Wrapper for mobile layout */}
                                                <div className="resource-main-content">
                                                    <DualStatusIndicator 
                                                        hasCoordinates={!!r.coordinates} 
                                                        hasGeohash={!!r.geohash} 
                                                        hasCity={!!r.city}
                                                        hasState={!!r.state}
                                                    />
                                                    <div className="resource-info">
                                                        <h3 className="resource-name">{r.organizationName}</h3>
                                                        {r.city && r.state && (
                                                            <span className="resource-location">
                                                                {r.city}, {r.state}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="resource-actions">
                                                    <button className="btn btn-approve" onClick={() => handleApprove(r.id)}>Approve</button>
                                                    <Link to={`/edit-resource/${r.id}`} className="btn btn-edit">Edit</Link>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                        
                        <div className="resources-section">
                            <h2 className="list-header">
                                {currentUser?.role === 'admin' ? "Approved Resources" : "My Resources"} ({filteredApprovedResources.length})
                            </h2>
                            {filteredApprovedResources.length === 0 ? (
                                <p style={{ padding: '10px 20px', color: '#666' }}>No resources match your search.</p>
                            ) : (
                                <ResourceList resources={filteredApprovedResources} />
                            )}
                        </div>
                    </>
                )}
                {activeTab === 'categoryManagement' && <ManageCategoriesPage />}
                {activeTab === 'userManagement' && <UserManagementPanel />}
                {activeTab === 'analytics' && <AnalyticsPanel />}
            </div>
        </div>
    );
}

export default DashboardPage;